import { CSSProperties, Fragment } from 'react';
import Link from 'next/link';
import { CircleCheck } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTasks, type WebTask } from '../../../lib/tasks';
import {
  AGENT_TASK_BUCKET_BI_SLUG,
  awaitsDecision,
  bucketOfTask,
  bucketTasks,
  sortedForTaskList,
  type AgentTaskBucket,
} from '../../../lib/agent-task-buckets';
import { statusInk, taskStatusBadge } from '../../../lib/task-status';
import { useMyAgents } from '../../../lib/my-agents';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { OutreachThreadModal } from '../tasks/OutreachThreadModal';
import { Badge, Button, Card } from '../ui';
import { AgentDot } from '../../AgentDot';
import styles from './Home2TasksCard.module.css';

// Home 2 (symphonyHome2Web) — the Tasks bento card (Figma 1859:2755). A preview of the top few tasks,
// each with its status dot + title + assigned-agent tag and a status chip, over a summary line and an
// "Open Tasks" action. Reuses the shared task model end to end: `useTasks` for the list, the
// bucket helpers for ordering + counts, `StatusBadge` for the chip, and `statusInk` so the dot wears
// the same colour token the chip does. Hidden while loading or when there are no tasks.

// The card is the white sunken DS Card WITH the separator border the bento cards carry (no DS skin is
// white-plus-border, so the border colour is set here over the sunken skin's transparent one).
const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' };
const HEADER_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.32px',
  color: 'rgb(var(--ink-rgb))',
};

const LIST: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' };
const ROW_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 };
const DOT: CSSProperties = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 };
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};
const NAME_DONE: CSSProperties = { textDecoration: 'line-through', color: 'var(--ds-text-secondary)' };
const SEP: CSSProperties = { fontSize: 13, color: 'rgb(var(--ink-rgb))', flexShrink: 0 };
const AGENT_TAG: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 };
const AGENT_NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.28px',
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
};

// `marginTop: auto` sinks the divider + footer to the card's bottom, so the footer stays aligned when
// the card is stretched to match a taller sibling in the row.
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0, marginTop: 'auto' };
const FOOTER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const SUMMARY: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const PREVIEW_COUNT = 3;

export function Home2TasksCard() {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useTasks();
  const teamState = useMyAgents();
  const { openModal } = useTaskSidePanel();

  if (state.kind !== 'ready' || state.tasks.length === 0) return null;

  const buckets = bucketTasks(state.tasks);
  const rows = sortedForTaskList(state.tasks).slice(0, PREVIEW_COUNT);
  const agents = teamState.status === 'ready' ? teamState.team.agents : [];
  const agentName = (id?: string): string | undefined =>
    id ? agents.find((a) => a.id === id)?.name?.trim() || undefined : undefined;

  // Opening a task shows its detail in the side panel (the web "task page"), mirroring TaskCard — an
  // outreach row opens its thread instead, since it has no AgentTask detail behind it.
  const openTask = (task: WebTask) => {
    const isOutreach = task.rowKind === 'outreach';
    logPageClickBi({ page_name: 'home', button_name: isOutreach ? 'open_outreach' : 'open_task', task_id: task.id });
    openModal(isOutreach ? <OutreachThreadModal task={task} /> : <TaskDetailModal taskId={task.id} />, task.id);
  };

  // Footer count groups → the matching tab on the tasks page (`?tab=<bucket>`, the camelCase value
  // `parseBucketParam` accepts). Only non-empty groups render; `failed` sits alongside the other three.
  const groups = (
    [
      { bucket: 'inProgress', count: buckets.inProgress.length, labelKey: 'home2.tasks.count.inProgress' },
      { bucket: 'waiting', count: buckets.waiting.length, labelKey: 'home2.tasks.count.needsYou' },
      { bucket: 'done', count: buckets.done.length, labelKey: 'home2.tasks.count.done' },
      { bucket: 'failed', count: buckets.failed.length, labelKey: 'home2.tasks.count.failed' },
    ] as { bucket: AgentTaskBucket; count: number; labelKey: string }[]
  ).filter((g) => g.count > 0);

  return (
    <Card style={CARD}>
      <div style={HEADER}>
        <span style={HEADER_LEFT}>
          <CircleCheck size={16} aria-hidden style={{ color: 'rgb(var(--ink-rgb))', flexShrink: 0 }} />
          <span style={TITLE}>{t('workspace.tasks.title')}</span>
        </span>
      </div>

      <div style={LIST}>
        {rows.map((task) => {
          // The row is bucketed by `bucketOfTask`, which promotes a task blocked on an open question to
          // waiting whatever its status says; the chip reads the same rule or the card contradicts itself.
          const badge = taskStatusBadge(task.status, { awaitingDecision: awaitsDecision(task) });
          const isDone = bucketOfTask(task) === 'done';
          const dotColor = statusInk(badge.variant);
          // A done task reads as finished — struck through, muted, and without its agent tag (Figma).
          const name = isDone ? undefined : agentName(task.agentId);
          // This card labels the waiting-on-you bucket "Needs you" rather than the shared "Action needed"
          // (scoped here only — the app-wide StatusBadge still reads `tasks.status.actionNeeded`).
          const chipLabel = badge.variant === 'waiting-on-you' ? t('home2.tasks.needsYou') : t(badge.labelKey);
          return (
            <div
              key={task.id}
              style={ROW}
              className={styles.taskRow}
              role="button"
              tabIndex={0}
              onClick={() => openTask(task)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openTask(task);
                }
              }}
            >
              <span style={ROW_LEFT}>
                <span style={{ ...DOT, background: dotColor }} />
                <span
                  className={`${styles.taskTitle}${isDone ? ` ${styles.done}` : ''}`}
                  style={isDone ? { ...NAME, ...NAME_DONE } : NAME}
                >
                  {task.title}
                </span>
                {name && (
                  <>
                    <span style={SEP}>·</span>
                    <span style={AGENT_TAG}>
                      <AgentDot id={task.agentId!} size={15} />
                      <span style={AGENT_NAME}>{name}</span>
                    </span>
                  </>
                )}
              </span>
              <Badge variant={badge.variant} label={chipLabel} size="tiny" />
            </div>
          );
        })}
      </div>

      <div style={DIVIDER} />

      <div style={FOOTER}>
        <span style={SUMMARY}>
          {groups.map((g, i) => (
            <Fragment key={g.bucket}>
              {i > 0 && <span style={SEP}>{' · '}</span>}
              <Link
                href={`${basePath}/tasks?tab=${g.bucket}`}
                className={styles.groupLink}
                onClick={() =>
                  logPageClickBi({ page_name: 'home', button_name: `open_tasks_${AGENT_TASK_BUCKET_BI_SLUG[g.bucket]}` })
                }
              >
                {t(g.labelKey, { count: g.count })}
              </Link>
            </Fragment>
          ))}
        </span>
        <Button
          size="medium"
          href={`${basePath}/tasks`}
          onClick={() => logPageClickBi({ page_name: 'home', button_name: 'open_tasks' })}
        >
          {t('home2.tasks.open')}
        </Button>
      </div>
    </Card>
  );
}
